"use client";

import React from "react";
import Image from "next/image";
import s from "../styles.parts";
import Consts from "@common/config/consts";
import { dayjs, logjs, utils } from "@lib/client";
import { usePartsList } from "../usePartsList";

import { useRouter } from "next/navigation";

const log = new logjs("PartsLabelListCompleteArea");

/**
 * 部品ラベルリスト完了表示コンポーネント
 * partslabellist_complete.ctpをReactに移植した表示専用コンポーネント
 *
 * @param {Object} props - プロパティ
 * @param {Object} props.data - データオブジェクト（partsList, request, answer, partskind等を含む）
 * @param {string} props.quote_number - 見積番号
 * @param {Object} props.photoDialogProps - 写真ダイアログのプロパティ（open関数を含む）
 * @param {Object} props.qaDialogProps - 質問・回答ダイアログのプロパティ（open関数を含む）
 * @param {Object} props.fileupDialogProps - ファイルアップロードダイアログのプロパティ（open関数を含む）
 * @param {Object} props.resPict - 写真データのオブジェクト [PAID] => 写真データ
 * @param {Object} props.resFile - ファイルデータのオブジェクト [PAID] => ファイルデータ
 * @param {Object} props.prMySeiyaku - 自社成約フラグ [PRID] => Hacchu_flg
 * @param {Object} props.prSeiyaku - 成約フラグ [PRID] => 成約フラグ
 * @param {Object} props.AqbData - 在庫確認データ [PAID] => [total, answered, ...]
 * @param {Function} props.onOpenHenpinModal - 返品値引処理モーダルを開く関数 (mitsumoriNo, paid) => void
 * @returns {JSX.Element} 部品ラベルリスト完了表示コンポーネント
 */
export default function PartsLabelListCompleteArea({
  data,
  quote_number,
  state,
  actions,
  photoDialogProps,
  henpinDialogProps,
  fileupDialogProps,
  qaDialogProps,
  qaabDialogProps,
  setEditPartsData,
  editPartsData,
  resPict = {},
  resFile = {},
  prMySeiyaku: prMySeiyakuProp = {},
  prSeiyaku: prSeiyakuProp = {},
  AqbData = {},
  onOpenHenpinModal,
  reload,
}) {
  log.debug("START ");
  const router = useRouter();

  // 共通関数の取得
  const [form, formProps] = usePartsList({ AqbData: AqbData ?? {} }, state, actions);

  // data を優先し、なければ props を使う（他画面と揃える）
  const prSeiyaku = data?.prSeiyaku || prSeiyakuProp || {};
  const prMySeiyaku = data?.prMySeiyaku || prMySeiyakuProp || {};

  // 現車と紐付いている場合は色変える
  const isGensha = data?.answer?.[0]?.RequestAnswer?.GenshaID ? true : false;

  // 見積期限が現在時刻以降かどうか
  const isRequestLimitValid = data?.request?.Request_Limit && (dayjs(data.request.Request_Limit).isAfter(dayjs()) || dayjs(data.request.Request_Limit).isSame(dayjs(), "day"));

  // 依頼期限の判定
  const isAfterCarryTaxinDate = formProps.isAfterCarryTaxinDate(data?.request?.Request_Limit);

  if (!data || !data.partsList) {
    return null;
  }
  const _editPartsData = (paid, index, target_key, value, baseItem) => {
    setEditPartsData((prev) => {
      // 既にあればそれを使う、なければ元データをベースにする
      const currentRow = prev[paid] || { ...baseItem };

      const updatedRow = {
        ...currentRow,
        [target_key]: value,
      };

      return {
        ...prev,
        [paid]: updatedRow,
      };
    });
  };

  return (
    <>
      {/* ヘッダーセクション */}
      <s.PartsTitle>
        部品情報
          <s.QuestionButton
            data-key={data?.partsList?.[0]?.id || ""}
            onClick={(e) => {
              e.preventDefault();
              qaDialogProps?.open?.({
                MitsumoriNo: quote_number,
                isQuestion: state.user?.Gyoshu_flg === 1,
                request: data?.request,
                answer: data?.answer,
              });
            }}
          >
            依頼元へ質問を行う | 回答を見る
          </s.QuestionButton>
          {isGensha && (
            <s.StockedVehicleButton style={{ verticalAlign: "middle", marginTop: "-5px" }} className="important-btn4">
              <Image src="/images/gensha_icon_w.png" alt="入庫済み車輌" width={16} height={16} />
              入庫済みの車輌があります
            </s.StockedVehicleButton>
          )}
      </s.PartsTitle>

      {isAfterCarryTaxinDate && (
        <strong style={{ color: "#0000ff", fontSize: "12pt" }}>　※上記の車両情報（グレード、カラ―等）につきましては、念の為、車台番号から再度ご確認のうえ、お見積りをお願い致します。</strong>
      )}

      {data?.answer?.[0]?.UserName && (
        <s.FinalAnswererContainer>
          <s.FinalAnswererLabel>最終回答者： {data.answer[0].UserName || ""} 様</s.FinalAnswererLabel>
        </s.FinalAnswererContainer>
      )}

      <s.PLTable>
        <tbody>
          {(() => {
            const rows = [];
            const partsList = data.partsList || [];

            for (let i = 0; i < partsList.length; i++) {
              const partsItem = partsList[i];
              log.debug("partsItem", partsItem);
              const PA_PRID = partsItem?.PRID; // partsItem?.PartsAnswer?.PRID
              const isPict = partsItem.isPict;
              const isFile = partsItem.isFile;

              // PA_PRID == 0 かつ StanderdPrice が空の場合はスキップ
              if (PA_PRID === 0 && !partsItem?.StanderdPrice) {
                // partsItem?.PartsAnswer?.StanderdPrice
                continue;
              }

              // 他社成約の判定
              let thCol = 16;
              let thColor = "";
              let showOtherCompany = false;

              if (PA_PRID > 0) {
                const prid = PA_PRID;
                const hacchuflg = prMySeiyaku[prid];
                const seiyaku = prSeiyaku[prid];
                if (
                  seiyaku &&
                  hacchuflg !== Consts.HACCHU_FLG.KAKUTEI &&
                  hacchuflg !== Consts.HACCHU_FLG.HASOZUMI &&
                  hacchuflg !== Consts.HACCHU_FLG.NEBIKI &&
                  hacchuflg !== Consts.HACCHU_FLG.HENPIN
                ) {
                  thCol = 14;
                  thColor = "border-left-style:none; background:#696969; color: #FFF;";
                  showOtherCompany = true;
                }
              }

              const PartsNameHeader = isGensha ? s.PartsNameHeaderGensha : s.PartsNameHeaderNormal;

              const Bikou = data.partsRList?.find((item) => item.id === partsItem.PRID)?.Bikou;

              // 部品名ヘッダー行
              rows.push(
                <tr key={`header-${i}`}>
                  {showOtherCompany && (
                    <s.PartsNameHeaderGensha
                      colSpan="2"
                      scope="row"
                      style={{ borderRightStyle: "none", background: "#696969", textAlign: "center", verticalAlign: "middle" }}
                    >
                      <Image src="/images/tasha.png" alt="他社成約" width={120} height={34} />
                    </s.PartsNameHeaderGensha>
                  )}
                  <PartsNameHeader
                    colSpan={thCol}
                    scope="row"
                    style={
                      thColor
                        ? {
                            borderLeftStyle: "none",
                            background: "#696969",
                            color: "#FFF",
                          }
                        : undefined
                    }
                  >
                    {PA_PRID === 0 ? (
                      <>
                        {partsItem?.OthPartsName || ""} {/* partsItem?.PartsAnswer?.OthPartsName */}
                        {partsItem?.OthPartsSonota && <>　品名：{partsItem.OthPartsSonota}</>} {/* partsItem?.PartsAnswer?.OthPartsSonota */}
                      </>
                    ) : (
                      <>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "5px" }}>
                          {partsItem?.P_PartsName}&nbsp;
                          {/* 部品カテゴリアイコン */}
                          {(() => {
                            const partskind = state?.initData?.partskind || [];
                            let pk3flg = 0;
                            const pkids = [partsItem?.P_PKID1, partsItem?.P_PKID2, partsItem?.P_PKID3, partsItem?.P_PKID4, partsItem?.P_PKID5];

                            return (
                              <>
                                {pkids.map((pkid, idx) => {
                                  if (!pkid || pkid === 3) {
                                    if (pkid === 3) pk3flg = 1;
                                    return null;
                                  }
                                  const categoryName = formProps.getCategoryName(pkid, partskind);
                                  const categoryStyle = formProps.getCategoryStyle(pkid);
                                  return (
                                    <s.PartsCategoryIcon key={idx} style={categoryStyle}>
                                      {categoryName}
                                    </s.PartsCategoryIcon>
                                  );
                                })}
                                {pk3flg === 1 && (
                                  <>
                                    <span style={{ marginLeft: "10px" }}>　　</span>
                                    <s.PartsCategoryIcon style={formProps.getCategoryStyle(3)}>{formProps.getCategoryName(3, partskind)}</s.PartsCategoryIcon>
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        {Bikou ? <p style={{ margin: "5px 0 0 0", fontSize: "14px", fontWeight: "normal" }}>({Bikou})</p> : null}
                      </>
                    )}
                  </PartsNameHeader>
                </tr>
              );

              // テーブルヘッダー行
              rows.push(
                <tr key={`header-row-${i}`}>
                  <s.PLHeadTh2>&nbsp;</s.PLHeadTh2>
                  <s.PLHeadTh2 scope="row">純正品番</s.PLHeadTh2>
                  <s.PLHeadTh2>数量</s.PLHeadTh2>
                  <s.PLHeadTh2>部品カテゴリ</s.PLHeadTh2>
                  <s.PLHeadTh2>価格(税別)</s.PLHeadTh2>
                  {isAfterCarryTaxinDate ? (
                    <s.PLHeadTh2 style={{ backgroundColor: "#FFF666" }}>送料(税別)</s.PLHeadTh2>
                  ) : (
                    <s.PLHeadTh2>
                      送料
                      <span style={{ fontWeight: "bold", backgroundColor: "rgb(255, 255, 0)" }}>(税込)</span>
                    </s.PLHeadTh2>
                  )}
                  <s.PLHeadTh2>発送可能日</s.PLHeadTh2>
                  <s.PLHeadTh2>カラー番号</s.PLHeadTh2>
                  <s.PLHeadTh2>カラー名称</s.PLHeadTh2>
                  <s.PLHeadTh2>写真</s.PLHeadTh2>
                  <s.PLHeadTh2>部品備考</s.PLHeadTh2>
                  <s.PLHeadTh2 scope="row">自社管理番号</s.PLHeadTh2>
                  <s.PLHeadTh2>発送方法</s.PLHeadTh2>
                  <s.PLHeadTh2>運送会社・伝票番号</s.PLHeadTh2>
                  <s.PLHeadTh2>発送日</s.PLHeadTh2>
                  <s.PLHeadTh2>返品値引処理</s.PLHeadTh2>
                </tr>
              );

              // 回答データ行
              (() => {
                let hasDisplay = false;
                const dataRows = [];
                let j = i;

                for (; j < partsList.length; j++) {
                  const item = partsList[j];
                  const itemPaid = item?.id; // item?.PartsAnswer?.id

                  if (item?.StanderdPrice) {
                    // item?.PartsAnswer?.StanderdPrice
                    hasDisplay = true;

                    const paid = item.id; // item.PartsAnswer.id

                    const row = utils.hasOwn(editPartsData, paid) ? editPartsData[paid] : item;
                    log.debug(`paid:${paid} row:`, row);

                    const dataLimit = formProps.getLaterLimit(data?.request?.Request_Limit, row?.Sell_Limit);
                    const btNameQab = formProps.getQabButtonNameWithAqb(paid, dataLimit);
                    const hacchuFlg = item.Hacchu_flg; // item.PartsAnswer.Hacchu_flg
                    const { jyotai, bgcolor } = formProps.getHacchuStatus(hacchuFlg);

                    // 価格データ
                    // PHPテンプレートでは partsList[$i][0]['PartsPrice'] と partsList[$i][0]['Soryo'] を使用
                    let partsPrice = "";
                    let soryo = 0;
                    /*
                    if (Array.isArray(item) && item[0]) {
                      // partsList[i][0]の構造
                      partsPrice = item[0]?.PartsPrice || "";
                      soryo = item[0]?.Soryo !== undefined ? item[0].Soryo : 0;
                    } else if (item?.StanderdPrice) {
                      // item?.PartsAnswer?.StanderdPrice
                      // 直接PartsAnswerにStanderdPriceがある場合
                      partsPrice = item.StanderdPrice; // item.PartsAnswer.StanderdPrice
                      soryo = item?.DeliveryPrice !== undefined ? item.DeliveryPrice : 0; // item?.PartsAnswer?.DeliveryPrice
                    } else if (item?.Price) {
                      // item?.PartsAnswer?.Price
                      // PartsAnswer.Priceがある場合
                      partsPrice = item.Price; // item.PartsAnswer.Price
                      soryo = item?.Soryo !== undefined ? item.Soryo : item?.DeliveryPrice || 0; // item?.PartsAnswer?.Soryo, item?.PartsAnswer?.DeliveryPrice
                    }
                    */

                    partsPrice = item?.PartsPrice ?? "";
                    soryo = item?.Soryo !== undefined ? item?.Soryo : 0;
                    
                    log.debug("ckeck ,******item", item);
                    log.debug("ckeck ,******partsPrice", partsPrice);

                    // 発送情報の表示内容を取得
                    // PHPテンプレート: if($partsList[$i]['PartsAnswer']['Haiso_kubun'] != 1 or ($partsList[$i]['PartsAnswer']['Haiso_kubun'] == 1 and $partsList[$i]['PartsAnswer']['Haiso_Corp']))
                    const renderShippingInfo = () => {
                      const haisoKubun = item?.Haiso_kubun; // item?.PartsAnswer?.Haiso_kubun
                      const haisoCorp = item?.Haiso_Corp || ""; // item?.PartsAnswer?.Haiso_Corp
                      const haisoDenpyo = item?.Haiso_denpyo || ""; // item?.PartsAnswer?.Haiso_denpyo

                      // 発送方法が「発送（1）」で運送会社が空の場合は入力フォームを表示
                      // それ以外の場合は既存データを表示
                      if (haisoKubun === Consts.HAISO_KUBUN.HASSO) {
                        return (
                          <s.ConfirmTableCell style={{ backgroundColor: haisoCorp ? "#ffffff" : "#e5ffe5" , textAlign: haisoCorp ? "center" : "left" }}>                            
                            {/* 運送会社 */}
                            {haisoCorp ? (
                              haisoCorp
                            ) : (
                              formProps.input_Haiso_Corp({
                                value: row?.Haiso_Corp || "",
                                placeholder: "例）西濃運輸",
                                onChange: (e) => {
                                  _editPartsData(paid, j, "Haiso_Corp", e.target.value, item);
                                },
                                style: { width: "150px" },
                              })
                            )}

                            <br />

                            {/* 伝票番号 */}
                            {haisoDenpyo ? (
                              haisoDenpyo
                            ) : (
                              formProps.input_Haiso_denpyo({
                                value: row?.Haiso_denpyo || "",
                                placeholder: "例）12345678",
                                onChange: (e) => {
                                  _editPartsData(paid, j, "Haiso_denpyo", e.target.value, item);
                                },
                                style: { width: "8em" },
                              })
                            )}

                          </s.ConfirmTableCell>
                        );
                      }

                      // それ以外の場合は既存データを表示
                      return (
                        <s.ConfirmTableCell>
                          {haisoCorp || null}
                          {haisoCorp && haisoDenpyo && <br />}
                          {haisoDenpyo || null}
                        </s.ConfirmTableCell>
                      );
                    };

                    // 返品値引処理の表示内容を取得
                    // PHPテンプレート: 発送済または値引の場合、発送日から8日以内の場合のみボタンを表示
                    // 値引の場合は常に「値引」テキストを表示
                    const renderHenpinShori = () => {
                      // 発送済または値引の場合
                      if (hacchuFlg === Consts.HACCHU_FLG.HASOZUMI || hacchuFlg === Consts.HACCHU_FLG.NEBIKI) {
                        const haisoDate = item?.Haiso_Date; // item?.PartsAnswer?.Haiso_Date
                        const daysSinceHaiso = formProps.getDaysSinceHaiso(haisoDate);

                        // 発送日から8日以内の場合のみ「返品値引処理」ボタンを表示
                        const shouldShowButton = daysSinceHaiso !== null && daysSinceHaiso < 8;

                        const hasContent = shouldShowButton || hacchuFlg === Consts.HACCHU_FLG.NEBIKI;
                        if (!hasContent) {
                          return null;
                        }

                        return (
                          <>
                            {shouldShowButton && (
                              <s.GrayBtn
                                onClick={(e) => {
                                  henpinDialogProps.open(
                                    {
                                      MitsumoriNo: quote_number,
                                      Paid: itemPaid,
                                    },
                                    (res) => {
                                      log.debug("onClose res", res);
                                      reload();
                                      /*
                                      if (typeof window !== "undefined") {
                                        window.location.reload();
                                      }
                                        */
                                    }
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                返品値引処理
                              </s.GrayBtn>
                            )}
                            {hacchuFlg === Consts.HACCHU_FLG.NEBIKI && (
                              <>
                                {shouldShowButton && <br />}
                                値引
                              </>
                            )}
                          </>
                        );
                      } else if (hacchuFlg === Consts.HACCHU_FLG.HENPIN) {
                        // 返品の場合
                        return "返品処理済";
                      }

                      return null;
                    };

                    // 送料の表示（返品の場合は「送料込」を表示しない）
                    // PHPテンプレート: ($partsList[$i][0]['Soryo'] == 0 and $partsList[$i]['PartsAnswer']['Hacchu_flg'] != HACCHU_FLG_HENPIN) ? "送料込" : number_format( $partsList[$i][0]['Soryo'] )."円"
                    const renderSoryo = () => {
                      // 返品の場合は送料を表示（0でも「0円」と表示）
                      if (hacchuFlg === Consts.HACCHU_FLG.HENPIN) {
                        return `${formProps.formatNumber(soryo || 0)}円`;
                      } else {
                        // それ以外の場合は通常のロジック
                        return soryo === 0 ? "送料込" : soryo ? `${formProps.formatNumber(soryo)}円` : null;
                      }
                    };

                    dataRows.push(
                      <tr key={`data-${j}`} {...(bgcolor ? { className: bgcolor } : {})}>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle"}}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            {jyotai}
                            {jyotai && <br />}
                            {btNameQab && (
                                <Image
                                src={`/images/${btNameQab}.png`}
                                className="qabOpen btn_gray"
                                data-key={`${data.answer?.[0]?.RequestAnswer?.id}/${paid}`}
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  qaabDialogProps.open({
                                    mitsumoriNo: quote_number,
                                    raid: partsItem?.RAID || null,
                                    paid: partsItem?.id || null,
                                    isQuestion: state.user?.Gyoshu_flg === 2,
                                  });
                                }}
                                alt="在庫確認"
                                width={90}
                                height={24}
                              />
                            )}
                          </div>
                        </s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }} scope="row">
                          {item?.PartsJyunseiCode || null}
                        </s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{item?.Suryo || null}</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{formProps.getCategoryName(item?.PartsKindID, data.partskind)}</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{partsPrice !== "" ? `${formProps.formatNumber(partsPrice)}円` : null}</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{renderSoryo()}</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{formProps.getHassoStatusText(item?.Hasso)}</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{item?.ColorNo || null}</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{item?.ColorName || null}</s.ConfirmTableCell>
                        <s.ConfirmTableCell>
                          {(() => {
                            const mitsumoriNo = quote_number;
                            const seqnum = item?.Seqnum;
                            const PRID = item?.PRID;

                            if (PA_PRID === 0) {
                              return (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",}}>
                                  {isPict && (
                                    <s.FileIcon
                                      src="/images/camera_icon2.png"
                                      alt="写真"
                                      onClick={() =>
                                        photoDialogProps.open({
                                          mitsumoriNo: mitsumoriNo,
                                          prid: 0,
                                          seqnum: seqnum,
                                          refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                        })
                                      }
                                    />
                                  )}
                                  {isFile && (
                                    <s.FileIcon
                                      src="/images/file_icon.png"
                                      alt="ファイル"
                                      onClick={formProps.handleFileupClick(fileupDialogProps, mitsumoriNo, 0, seqnum, Consts.WRECKER_PHOTO.YUKO_REF,)}
                                      style={{ cursor: "pointer" }}
                                    />
                                  )}
                                </div>
                              );
                            } else {
                              return (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",}}>
                                  {isPict && (
                                    <s.FileIcon
                                      src="/images/camera_icon2.png"
                                      alt="写真"
                                      onClick={() =>
                                        photoDialogProps.open({
                                          mitsumoriNo: mitsumoriNo,
                                          prid: PRID,
                                          seqnum: seqnum,
                                          refMode: Consts.WRECKER_PHOTO.YUKO_REF,
                                          data:data
                                        })
                                      }
                                    />
                                  )}

                                  {isFile && (
                                    <s.FileIcon
                                      src="/images/file_icon.png"
                                      alt="ファイル"
                                      onClick={formProps.handleFileupClick(
                                        fileupDialogProps,
                                        mitsumoriNo,
                                        PRID,
                                        seqnum,
                                        Consts.WRECKER_PHOTO.YUKO_REF
                                      )}
                                      style={{ cursor: "pointer" }}
                                    />
                                  )}
                                </div>
                              );
                            }
                          })()}
                        </s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle", whiteSpace: "pre-line", textAlign: "left" }}>{item?.Bikou || null}</s.ConfirmTableCell>
                        {bgcolor ? (
                          <s.ConfirmTableCell style={{ verticalAlign: "middle" }} scope="row">
                            {item?.JishaNo || null}
                          </s.ConfirmTableCell>
                        ) : (
                          <s.CompanyCodeCellBg style={{ verticalAlign: "middle" }} scope="row">
                            {item?.JishaNo || null}
                            {(item?.JishaNo || item?.PrepKind) && <br />}
                            {item?.PrepKind === Consts.GOODS.INHOUSE && <label>自社品</label>}
                            {item?.PrepKind === Consts.GOODS.OTHER && <label>他社品</label>}
                          </s.CompanyCodeCellBg>
                        )}
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{formProps.getHaisoKubunText(item?.Haiso_kubun)}</s.ConfirmTableCell>
                        {renderShippingInfo()}
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{formProps.formatDate(item?.Haiso_Yotei) || null}</s.ConfirmTableCell>
                        <s.ConfirmTableCell style={{ verticalAlign: "middle" }}>{renderHenpinShori()}</s.ConfirmTableCell>
                      </tr>
                    );
                  }

                  // グループの終了判定
                  const nextItem = partsList[j + 1];
                  if (
                    j + 1 >= partsList.length ||
                    (PA_PRID === 0 && item?.OthPartsName !== nextItem?.OthPartsName) || // item?.PartsAnswer?.OthPartsName
                    (PA_PRID !== 0 && PA_PRID !== nextItem?.PRID) // nextItem?.PartsAnswer?.PRID
                  ) {
                    // 未回答行の表示（PHPテンプレートでは16列）
                    if (!hasDisplay && PA_PRID !== 0) {
                      dataRows.push(
                        <s.NoneRow key={`none-${i}`}>
                          <s.ConfirmTableCell scope="row">-</s.ConfirmTableCell>
                          <s.ConfirmTableCell scope="row">-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell scope="row">-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                          <s.ConfirmTableCell>-</s.ConfirmTableCell>
                        </s.NoneRow>
                      );
                    }

                    i = j;
                    break;
                  }
                }

                // dataRowsをrows配列に追加
                rows.push(...dataRows);
              })();
            }

            return rows;
          })()}
        </tbody>
      </s.PLTable>
    </>
  );
}
